import type { TranscriptFlagItem, VisitTranscriptFlags } from "@rosc/contracts";
import { Sparkles } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranscriptFlags } from "@/hooks/visits/useVisitReview";
import { splitFlagged } from "@/lib/review/transcriptFlags";
import { cn } from "@/lib/utils";

// AI flags on the transcript (v3.2, VITE_TRANSCRIPT_FLAGS_ENABLED): red
// clinical risk, amber unclear audio, blue auto-fixed. The worker makes them
// after the note; here they are inline marks in the transcript, a legend and
// a count. Each mark opens why it was flagged.

const FlagsContext = createContext<VisitTranscriptFlags | null>(null);

export const TranscriptFlagsProvider = ({
  visitPublicId,
  revision,
  enabled,
  children,
}: {
  visitPublicId: string | undefined;
  // Changes when the transcripts or note change, to read the flags again.
  revision: string;
  enabled: boolean;
  children: ReactNode;
}): JSX.Element =>
  // Off, nothing is read (and no query client is needed).
  enabled ? (
    <ReadFlags revision={revision} visitPublicId={visitPublicId}>
      {children}
    </ReadFlags>
  ) : (
    <>{children}</>
  );

const ReadFlags = ({
  visitPublicId,
  revision,
  children,
}: {
  visitPublicId: string | undefined;
  revision: string;
  children: ReactNode;
}): JSX.Element => {
  const { data } = useTranscriptFlags(visitPublicId, revision, true);
  return (
    <FlagsContext.Provider value={data ?? null}>
      {children}
    </FlagsContext.Provider>
  );
};

// Static flags (stories, tests).
export const TranscriptFlagsValue = FlagsContext.Provider;

export const useVisitTranscriptFlags = (): VisitTranscriptFlags | null =>
  useContext(FlagsContext);

// The flags of one transcript: null is the visit's own recording.
export const useTranscriptFlagsFor = (
  recordingPublicId: string | null,
): TranscriptFlagItem[] => {
  const flags = useContext(FlagsContext);
  return useMemo(
    () =>
      flags?.transcripts.find(
        (transcript) => transcript.recordingPublicId === recordingPublicId,
      )?.flags ?? [],
    [flags, recordingPublicId],
  );
};

const MARK: Record<TranscriptFlagItem["severity"], string> = {
  red: "rounded-[4px] bg-v3-rec-tint px-0.5 text-v3-rec-text shadow-[inset_0_-1.5px_0_var(--v3-rec)] hover:bg-[color-mix(in_srgb,var(--v3-rec)_18%,transparent)]",
  amber:
    "rounded-[4px] bg-v3-warn-tint px-0.5 text-inherit shadow-[inset_0_-1.5px_0_var(--v3-warn)] hover:bg-[color-mix(in_srgb,var(--v3-warn)_22%,transparent)]",
  blue: "text-inherit underline decoration-v3-brand decoration-dotted decoration-2 underline-offset-4 hover:bg-v3-brand-tint",
};
const DOT: Record<TranscriptFlagItem["severity"], string> = {
  red: "bg-v3-rec",
  amber: "bg-v3-warn",
  blue: "bg-v3-brand",
};
const KIND_TEXT: Record<TranscriptFlagItem["severity"], string> = {
  red: "text-v3-rec-text",
  amber: "text-v3-warn-text",
  blue: "text-v3-brand-strong",
};

const FlagMark = ({
  flag,
  children,
}: {
  flag: TranscriptFlagItem;
  children: ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();
  const severity = t(`visit.flags.severity.${flag.severity}`);
  return (
    <Popover>
      <PopoverTrigger asChild>
        {/* A highlighter mark that stays in the line flow (a button would
            wrap as one centred box). */}
        {/* biome-ignore lint/a11y/useSemanticElements: a <button> is an atomic inline box, so a mark spanning two lines would be centred as a block; this span keeps the text flow and has the button role, focus and keys. */}
        <span
          aria-label={t("visit.flags.markAria", {
            severity,
            text: flag.quote,
          })}
          className={cn(
            "inline cursor-pointer rounded-[4px] text-left align-baseline [box-decoration-break:clone] [-webkit-box-decoration-break:clone] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring",
            MARK[flag.severity],
          )}
          data-severity={flag.severity}
          data-testid="transcript-flag"
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.currentTarget.click();
            }
          }}
        >
          {children}
        </span>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[320px] max-w-[calc(100vw-24px)] rounded-xl border-0 bg-v3-panel p-3.5 font-v3 text-v3-text shadow-v3-pop"
        collisionPadding={12}
      >
        <p
          className={cn(
            "flex items-center gap-2 text-[13px] font-semibold",
            KIND_TEXT[flag.severity],
          )}
        >
          <span
            aria-hidden
            className={cn("size-2 rounded-full", DOT[flag.severity])}
          />
          {severity}
          <span className="font-normal text-v3-text-3">
            · {t(`visit.flags.category.${flag.category}`)}
          </span>
        </p>
        <p className="mt-2 text-[14px] leading-[1.5] text-v3-text-2">
          “{flag.quote}”
          {flag.suggestion && (
            <>
              {" → "}
              <ins className="rounded-[5px] bg-v3-ok-tint px-1.5 py-px font-semibold text-v3-ok-text no-underline">
                {flag.suggestion}
              </ins>
            </>
          )}
        </p>
        <p className="mt-2 border-l-2 border-v3-border-strong pl-2.5 text-[13px] leading-[1.45] text-v3-text-2">
          {flag.reason}
        </p>
        <p className="mt-2.5 flex items-center gap-1.5 text-[11.5px] text-v3-text-3">
          <Sparkles aria-hidden className="size-3" />
          {flag.severity === "blue"
            ? t("visit.flags.autoFixedNote")
            : t("visit.flags.aiNote")}
        </p>
      </PopoverContent>
    </Popover>
  );
};

// Text with the flags inside it shown as marks (offsets in `text`).
export const FlaggedText = ({
  text,
  flags,
}: {
  text: string;
  flags: readonly TranscriptFlagItem[];
}): JSX.Element => {
  if (!flags.length) return <>{text}</>;
  return (
    <>
      {splitFlagged(text, flags).map((piece, index) =>
        piece.flag ? (
          <FlagMark key={piece.flag.key} flag={piece.flag}>
            {piece.text}
          </FlagMark>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: static pieces
          <span key={index}>{piece.text}</span>
        ),
      )}
    </>
  );
};

// "AI flags · 3" with the red/amber/blue legend, above the transcript.
export const TranscriptFlagsLegend = ({
  className,
}: {
  className?: string;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const flags = useVisitTranscriptFlags();
  if (!flags || flags.transcripts.every((item) => item.state === "none"))
    return null;
  const { red, amber, blue } = flags.counts;
  const total = red + amber + blue;
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-4 gap-y-1.5 font-v3 text-[12.5px] text-v3-text-2",
        className,
      )}
      data-testid="transcript-flags-legend"
    >
      <span className="inline-flex items-center gap-1.5 font-semibold text-v3-text">
        <Sparkles aria-hidden className="size-3.5 text-v3-text-3" />
        {total
          ? t("visit.flags.count", { count: total })
          : t("visit.flags.none")}
      </span>
      {(
        [
          ["red", red, "bg-v3-rec-tint shadow-[inset_0_-2px_0_var(--v3-rec)]"],
          [
            "amber",
            amber,
            "bg-v3-warn-tint shadow-[inset_0_-2px_0_var(--v3-warn)]",
          ],
          [
            "blue",
            blue,
            "h-[7px] rounded-none border-b-2 border-dotted border-v3-brand",
          ],
        ] as const
      ).map(([severity, count, swatch]) => (
        <span key={severity} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className={cn("h-2.5 w-3.5 rounded-[3px]", swatch)}
          />
          {t(`visit.flags.severity.${severity}`)}
          <span className="font-mono tabular-nums text-v3-text-3">{count}</span>
        </span>
      ))}
    </div>
  );
};

// Compact count for the collapsed transcript bar: a dot per severity present.
export const TranscriptFlagsCount = (): JSX.Element | null => {
  const { t } = useTranslation();
  const flags = useVisitTranscriptFlags();
  if (!flags) return null;
  const { red, amber, blue } = flags.counts;
  const total = red + amber + blue;
  if (!total) return null;
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-v3-surface-2 px-2.5 py-0.5 font-v3 text-xs font-medium text-v3-text-2"
      data-testid="transcript-flags-count"
      title={t("visit.flags.countTitle", { red, amber, blue })}
    >
      {(["red", "amber", "blue"] as const)
        .filter((severity) => flags.counts[severity] > 0)
        .map((severity) => (
          <span
            key={severity}
            aria-hidden
            className={cn("size-1.5 rounded-full", DOT[severity])}
          />
        ))}
      {t("visit.flags.count", { count: total })}
    </span>
  );
};
